import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EconomicGroupResponse } from '@interfaces/economic-groups/responses/economic-group.response';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { Permissions } from '@app/core/guard/Permissions';
import {
  CompanySectorFieldComponent,
  NO_COMPANY_SECTOR,
  toCompanySectorPayload,
} from '@src/app/features/dashboard/company/company-register/company-sector-field/company-sector-field.component';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-create-edit-group-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, CompanySectorFieldComponent],
  templateUrl: './create-edit-group-modal.component.html',
  styleUrls: ['./create-edit-group-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEditGroupModalComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('dialog') dialogRef!: DialogComponent;

  @Output() saved = new EventEmitter<void>();

  form!: FormGroup;
  /** Setor padronizado (card 3642): o campo só aparece com `COMPANYSECTOR_LIST`. */
  readonly companySectorPermissions = Permissions.CompanySector;
  isEditMode = false;
  private editId: string | null = null;

  get dialogTitle(): string {
    return this.isEditMode ? 'Editar Grupo Econômico' : 'Novo Grupo Econômico';
  }

  constructor(
    translateService: TranslateService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly toast: ToastHelperService,
    private readonly formErrorHandler: FormErrorHandlerService,
    private readonly groupsService: EconomicGroupsHttpService
  ) {
    super(translateService);
    this.buildForm();
  }

  ngOnInit(): void {
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  open(group?: EconomicGroupResponse): void {
    this.form.reset({ companySectorId: NO_COMPANY_SECTOR });
    this.isEditMode = !!group;
    this.editId = group?.id ?? null;

    if (group) {
      this.form.patchValue({
        name: group.name,
        description: group.description ?? '',
        companySectorId: group.companySectorId || NO_COMPANY_SECTOR,
      });
      this.cdr.markForCheck();
    }

    this.dialogRef.open('', '', '', 'Cancelar', '', 'create-edit-group-modal', false, false);
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  submitAction(): void {
    if (!this.isFormValid()) {
      this.form.markAllAsTouched();
      this.toast.showToastError('Preencha o nome do grupo.');
      return;
    }

    this.isLoading = true;
    const { name, description, companySectorId } = this.form.getRawValue();
    const payload = {
      name: name?.trim(),
      description: description?.trim() || undefined,
      // Card 3642: "Sem setor" vai como null no cadastro e como Guid.Empty na edição (null manteria o setor).
      companySectorId: toCompanySectorPayload(companySectorId, this.isEditMode),
    };

    const request$ = this.isEditMode
      ? this.groupsService.update(this.editId!, payload)
      : this.groupsService.create(payload);

    request$
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            this.isEditMode
              ? 'Grupo econômico atualizado com sucesso.'
              : 'Grupo econômico criado com sucesso.'
          );
          this.saved.emit();
          this.dialogRef.close();
        },
        error: error => {
          this.formErrorHandler.showToast(error, 'Erro ao salvar grupo econômico.');
        },
      });
  }

  private buildForm(): void {
    this.form = this.fb.group({
      name: [null, [Validators.required, Validators.maxLength(200)]],
      description: [null, Validators.maxLength(1000)],
      companySectorId: [NO_COMPANY_SECTOR as string | null],
    });
  }
}
